import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

const DISPUTES_PATH_PATTERN = /\/finance\/disputes(\/|$)/;
const SETUP_SOURCE_PARAM = "setupSource";

/**
 * When a merchant lands on any disputes route while the flag is on AND
 * doesn't already have a `?setupSource=...` param (which the explicit
 * entry-source hook already handles), fire `onEnter` once per session
 * with a `'direct_navigation'` source so the provider creates a progress
 * row + opens intake.
 *
 * Guards in the provider's `handleEntrySource`:
 *   - Already-installed merchants (`disputes.enabled=true`) skip creation
 *     via `shouldCreateRowFromEntrySource` — no side effects for them.
 *   - Existing-row merchants skip creation (idempotent).
 *
 * `firedRef` only prevents rapid re-fires within the same session; the
 * row-existence guards above prevent duplicate creates regardless.
 */
export function useDisputesRouteAutoEntry(
  onEnter: (source: string) => void,
): void {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    // Explicit ?setupSource=... always wins — let the dedicated hook handle it.
    if (searchParams.get(SETUP_SOURCE_PARAM) != null) return;
    if (!DISPUTES_PATH_PATTERN.test(location.pathname)) return;
    firedRef.current = true;
    onEnter("direct_navigation");
  }, [location.pathname, searchParams, onEnter]);
}
